import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/store/store'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { UserFileRead } from '@/uppy-file-upload/schemas'
import { selectCurrentFolderId } from '@/user_files/folders/store/selectors'
import { UserFileProcessingBlockedReason, UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { FileListRequestState } from './slice'
import { isActiveExternalStorageItem } from '@/user_files/external-storage/utils/external-storage-items'
import { groupExtractedFiles } from '@/user_files/utils/group-extracted-files'

const IDLE_FILE_LIST_REQUEST: FileListRequestState = { status: 'idle', error: null }

/**
 * Actively synced files live on the External Files tab, not the Drive.
 * Tombstoned files (sync disconnected) count as regular Drive files.
 */
export function isExternalStorageLinkedFile(file: Pick<UserFileRead, 'external_storage'>): boolean {
  return isActiveExternalStorageItem(file)
}

const selectShouldShowExternalStorageItemsInDrive = (_state: RootState): boolean => kGetFeatureFlagStatus(kFeatureFlags.DRIVE_SHOW_EXTERNAL_STORAGE_ITEMS)
const selectIsDmsSourceEnabled = (_state: RootState): boolean => kGetFeatureFlagStatus(kFeatureFlags.DRIVE_SYNC)

// ============================================================================
// Base Selectors (raw state access)
// ============================================================================

/** Ordered file ids per folder (references into `filesById`). */
export const selectFilesByFolderId = (state: RootState) => state.userFilesState.filesByFolderId

export const selectFileListRequestsByFolderId = (state: RootState) => state.userFilesState.fileListRequestsByFolderId

/** Every known file record, keyed by id. Single source of truth. */
export const selectFilesById = (state: RootState) => state.userFilesState.filesById

/** All known file records keyed by id. Alias of `selectFilesById`. */
export const selectLoadedUserFilesById = selectFilesById

/** Ids a by-id fetch returned no record for (deleted, or not accessible to this member). */
export const selectUnavailableUserFileIds = (state: RootState) => state.userFilesState.unavailableFileIds

// ============================================================================
// Current Folder Files Selectors
// ============================================================================

function resolveFolderFiles(
  filesByFolderId: Record<string, string[]>,
  filesById: Record<string, UserFileRead>,
  folderId: string | null | undefined
): UserFileRead[] {
  if (!folderId) return []
  const ids = filesByFolderId[folderId]
  if (!ids) return []
  return ids.map((id) => filesById[id]).filter((file): file is UserFileRead => Boolean(file))
}

/**
 * Select files for a specific folder.
 * These are the backend-filtered browse rows currently loaded for that folder.
 */
export const selectFilesForFolder = createSelector(
  [selectFilesByFolderId, selectFilesById, (_state: RootState, props: { folderId: string | null | undefined }) => props.folderId],
  (filesByFolderId, filesById, folderId): UserFileRead[] => resolveFolderFiles(filesByFolderId, filesById, folderId)
)

/**
 * Select files for a specific folder, always alphabetically by filename.
 *
 * The alphabetical sort is load-bearing, not decorative: `filesByFolderId` is shared with the
 * drive, which fetches it in whatever order its sortable header is set to. Surfaces with no
 * sorting UI of their own — the add-files picker, the assistant's file-selection dialog — read
 * through here so the drive's sort can never reorder them.
 */
export const selectFilesForFolderAlphabetical = createSelector([selectFilesForFolder], (files): UserFileRead[] =>
  [...files].sort((a, b) => a.original_filename.localeCompare(b.original_filename))
)

/**
 * Select files for the current folder.
 * These are the backend-filtered browse rows currently loaded for that folder.
 */
export const selectCurrentFolderFiles = createSelector(
  [selectFilesByFolderId, selectFilesById, selectCurrentFolderId],
  (filesByFolderId, filesById, currentFolderId): UserFileRead[] => resolveFolderFiles(filesByFolderId, filesById, currentFolderId)
)

/**
 * Drive-visible files for the current folder.
 * Hides only actively synced files; tombstoned (disconnected) files stay visible.
 * Raw selectors intentionally keep external-storage records for pickers and other consumers.
 */
export const selectCurrentFolderDriveVisibleFiles = createSelector(
  [selectCurrentFolderFiles, selectShouldShowExternalStorageItemsInDrive, selectIsDmsSourceEnabled],
  (files, showExternalStorageItems, dmsSourceEnabled): UserFileRead[] => {
    if (showExternalStorageItems) return files
    if (!dmsSourceEnabled) return files.filter((file) => !isExternalStorageLinkedFile(file))
    const activeExternalFileIds = new Set(
      groupExtractedFiles(files)
        .filter(({ root }) => isExternalStorageLinkedFile(root))
        .map(({ file }) => file.id)
    )
    return files.filter((file) => !activeExternalFileIds.has(file.id))
  }
)

/**
 * Select whether files are currently loading for the current folder
 */
export const selectIsCurrentFolderFilesLoading = createSelector(
  [selectFileListRequestsByFolderId, selectCurrentFolderId],
  (fileListRequestsByFolderId, currentFolderId): boolean => {
    if (!currentFolderId) return false
    return fileListRequestsByFolderId[currentFolderId]?.status === 'loading'
  }
)

export const selectFileListRequest = (state: RootState, props: { folderId: string | null | undefined }): FileListRequestState => {
  if (!props.folderId) return IDLE_FILE_LIST_REQUEST
  return state.userFilesState.fileListRequestsByFolderId[props.folderId] ?? IDLE_FILE_LIST_REQUEST
}

/**
 * File ids in the current folder that still need folder-owned polling.
 * Folder state also owns pending extracted children, which have no browser upload in Uppy.
 */
export const selectCurrentFolderProcessingFileIds = createSelector([selectCurrentFolderFiles], (files): string[] =>
  files
    .filter(
      (file) =>
        (file.processing_status === UserFileProcessingStatus.Processing && file.processing_blocked_reason !== UserFileProcessingBlockedReason.OcrQuota) ||
        (file.processing_status === UserFileProcessingStatus.Pending && !!file.extracted_from_file_id)
    )
    .map((file) => file.id)
)
